import React from "react";
import PropTypes from "prop-types";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { IconButton, Tooltip } from "@mui/material";
import { cameraService } from "client-app-core";
import { getTranslation } from "orion-components/i18n";

const NestedMenu = ({
	nestedMenuContent,
	closeNestedMenu,
	nestedMenuIsFor,
	cameraId,
	closeMainMenu,
	onAddPreset,
	onDeletePreset,
	onBookmarkSelect,
	onDeleteBookmark,
	onClipSelect,
	onDeleteClip,
	onExportClip,
	canCreatePreset,
	isPresetValid,
	canDeletePreset
}) => {
	const hasNoExistingPresets = nestedMenuIsFor === "presets" && nestedMenuContent.length === 0;
	const hasNoExistingBookmarks = nestedMenuIsFor === "bookmarks" && nestedMenuContent.length === 0;
	const hasNoExistingClips = nestedMenuIsFor === "clips" && nestedMenuContent.length === 0;
	const menuWidth = nestedMenuIsFor === "clips" ? 190 : 160;

	const getClipLabel = clip => {
		if (clip?.name || clip?.description) {
			return clip.name || clip.description;
		}

		const startTime = new Date(clip?.startTime);
		const endTime = new Date(clip?.endTime);
		if (!Number.isNaN(startTime.getTime()) && !Number.isNaN(endTime.getTime())) {
			return `${startTime.toLocaleString()} - ${endTime.toLocaleTimeString()}`;
		}

		return "";
	};

	const nestedMenuActions = menuItem => {
		switch (nestedMenuIsFor) {
			case "presets": {
				const preset = menuItem.value;
				cameraService.gotoPreset(cameraId, preset, err => {
					if (err) {
						console.log("ERROR", err);
					}
				});
				closeMainMenu();
				closeNestedMenu();
				return null;
			}
			case "bookmarks":
				onBookmarkSelect && onBookmarkSelect(menuItem);
				return null;
			case "clips":
				onClipSelect && onClipSelect(menuItem);
				return null;
			default:
				return;
		}
	};
	return (
		<div
			style={{
				width: menuWidth,
				minHeight: 160,
				maxHeight: 280,
				overflowY: "auto",
				overflowX: "hidden",
				background: "#232223",
				borderRadius: 8,
				boxShadow: "0px 0px 6px #0000009A",
				padding: "4px 0",
				position: "relative",
				zIndex: 1001,
				overscrollBehavior: "contain"
			}}
			onWheel={event => event.stopPropagation()}
			data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-div"
		>
			{nestedMenuIsFor === "presets" && canCreatePreset && (
				<>
					<div
						className="bcc-main-menu"
						onClick={() => {
							onAddPreset && onAddPreset();
							closeMainMenu();
							closeNestedMenu();
						}}
						style={{
							width: "100%",
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "flex-end",
							minHeight: 20,
							padding: "2px 5px",
							fontSize: 10,
							lineHeight: "12px",
							cursor: "pointer",
							boxSizing: "border-box",
							fontFamily: "Roboto",
							color: "#4DB5F4"
						}}
						data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-div-2"
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 4,
								marginRight: 8,
								overflow: "hidden",
								whiteSpace: "nowrap",
								textOverflow: "ellipsis"
							}}
						>
							<AddIcon sx={{ width: 12, height: 12, color: "#4DB5F4" }} />
							{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.savePreset")}
						</div>
					</div>
				</>
			)}
			{!isPresetValid && nestedMenuIsFor === "presets" && (
				<div
					style={{
						color: "#FF0000",
						width: "100%",
						padding: "12px 16px",
						fontSize: 10,
						textAlign: "center",
						boxSizing: "border-box",
						fontFamily: "Roboto"
					}}
				>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.invalidPreset")}
				</div>
			)}
			{hasNoExistingPresets && isPresetValid && nestedMenuIsFor === "presets" && (
				<div
					style={{
						width: "100%",
						padding: "12px 16px",
						fontSize: 10,
						fontFamily: "Roboto",
						color: "#B5B9BE",
						textAlign: "center",
						boxSizing: "border-box"
					}}
				>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.noExistingPresetsFound")}
				</div>
			)}
			{hasNoExistingBookmarks && nestedMenuIsFor === "bookmarks" && (
				<div
					style={{
						width: "100%",
						padding: "12px 16px",
						fontSize: 10,
						fontFamily: "Roboto",
						color: "#B5B9BE",
						textAlign: "center",
						boxSizing: "border-box"
					}}
				>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.noExistingBookmarksFound")}
				</div>
			)}
			{hasNoExistingClips && nestedMenuIsFor === "clips" && (
				<div
					style={{
						width: "100%",
						padding: "12px 16px",
						fontSize: 10,
						fontFamily: "Roboto",
						color: "#B5B9BE",
						textAlign: "center",
						boxSizing: "border-box"
					}}
				>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.noExistingClipsFound")}
				</div>
			)}
			{nestedMenuContent.map((item, idx) =>
				item === "divider" ? (
					<div
						key={idx}
						style={{
							width: "90%",
							height: 1,
							backgroundColor: "#444",
							margin: "4px auto"
						}}
					></div>
				) : (
					<div
						key={idx}
						className="bcc-main-menu"
						onClick={() => nestedMenuActions(item)}
						style={{
							width: "100%",
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
							minHeight: 24,
							padding: "2px 5px",
							fontSize: 10,
							lineHeight: "12px",
							cursor: "pointer",
							boxSizing: "border-box",
							fontFamily: "Roboto"
						}}
						data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-div-3"
					>
						<div
							style={{
								flex: "1 1 auto",
								minWidth: 0,
								display: "flex",
								alignItems: "center",
								marginLeft: 10,
								marginRight: 4,
								overflow: "hidden",
								whiteSpace: "nowrap",
								textOverflow: "ellipsis"
							}}
						>
							{nestedMenuIsFor === "clips"
								? getClipLabel(item)
								: item?.label ||
									item?.name ||
									item?.description ||
									(typeof item === "string" ? item : "")}
						</div>
						{((nestedMenuIsFor === "presets" && canDeletePreset) ||
							(nestedMenuIsFor === "bookmarks" && onDeleteBookmark) ||
							(nestedMenuIsFor === "clips" && (onDeleteClip || onExportClip))) && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 2,
									flexShrink: 0
								}}
							>
								{nestedMenuIsFor === "clips" && onExportClip && (
									<Tooltip
										title={getTranslation(
											"global.CBComponents.newVideoPlayer.components.mainMenu.exportClip"
										)}
										placement="left"
										data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-tooltip"
									>
										<IconButton
											size="small"
											onClick={event => {
												event.stopPropagation();
												onExportClip(item);
											}}
											sx={{
												height: 14,
												width: 14,
												padding: 0,
												flexShrink: 0
											}}
											data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-icon-button"
										>
											<FileDownloadOutlinedIcon sx={{ width: 12, height: 12 }} />
										</IconButton>
									</Tooltip>
								)}
								{((nestedMenuIsFor === "presets" && canDeletePreset) ||
									(nestedMenuIsFor === "bookmarks" && onDeleteBookmark) ||
									(nestedMenuIsFor === "clips" && onDeleteClip)) && (
									<Tooltip
										title={
											nestedMenuIsFor === "presets"
												? getTranslation(
														"global.CBComponents.newVideoPlayer.components.mainMenu.deletePreset"
													)
												: nestedMenuIsFor === "bookmarks"
													? getTranslation(
															"global.CBComponents.newVideoPlayer.components.mainMenu.deleteBookmark"
														)
													: getTranslation(
															"global.CBComponents.newVideoPlayer.components.mainMenu.deleteClip"
														)
										}
										placement="left"
										data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-tooltip-2"
									>
										<IconButton
											size="small"
											onClick={event => {
												event.stopPropagation();
												if (nestedMenuIsFor === "presets") {
													onDeletePreset && onDeletePreset(item);
												} else if (nestedMenuIsFor === "bookmarks") {
													onDeleteBookmark && onDeleteBookmark(item);
												} else if (nestedMenuIsFor === "clips") {
													onDeleteClip && onDeleteClip(item);
												}
											}}
											sx={{
												height: 14,
												width: 14,
												padding: 0,
												flexShrink: 0
											}}
											data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-icon-button-2"
										>
											<DeleteIcon sx={{ width: 12, height: 12 }} />
										</IconButton>
									</Tooltip>
								)}
							</div>
						)}
					</div>
				)
			)}
			<div
				className="bcc-main-menu"
				onClick={closeNestedMenu}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 20,
					padding: "2px 0",
					cursor: "pointer",
					color: "#4DB5F4",
					fontSize: 10,
					lineHeight: "12px",
					borderTop: "1px solid #444"
				}}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-nested-menu-div-4"
			>
				{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.back")}
			</div>
		</div>
	);
};

NestedMenu.propTypes = {
	cameraId: PropTypes.string,
	canCreatePreset: PropTypes.bool,
	onAddPreset: PropTypes.func,
	onBookmarkSelect: PropTypes.func,
	onDeleteBookmark: PropTypes.func,
	onClipSelect: PropTypes.func,
	onDeleteClip: PropTypes.func,
	onExportClip: PropTypes.func,
	onDeletePreset: PropTypes.func,
	nestedMenuIsFor: PropTypes.string,
	closeMainMenu: PropTypes.func,
	nestedMenuContent: PropTypes.array.isRequired,
	closeNestedMenu: PropTypes.func.isRequired,
	isPresetValid: PropTypes.bool,
	canDeletePreset: PropTypes.bool
};

export default NestedMenu;
